import { Autocomplete, TextField, Typography, Stack } from '@mui/material';
import { Pets } from '@mui/icons-material';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import TaxExemptBadge from './TaxExemptBadge';

const UNASSIGNED_OPTION = {
  team_id: 'unassigned',
  name: '',
  formatted_address: '',
};

const RecipientAutocomplete = ({
  value,
  onChange,
  recipients = [],
  assignedRecipients = [],
  disabledRecipients = [],
  currentIndex,
  disabled = false,
  label,
  placeholder,
  unassignedLabel,
  includeUnassignedOption = true,
}) => {
  const { t } = useTranslation('translation');
  const openLabel = unassignedLabel || t('donations.unassigned');

  const options = useMemo(() => {
    const alreadyAssigned = new Set(
      assignedRecipients
        .filter((_, idx) => idx !== currentIndex)
        .map((r) => r.teamId)
        .filter((id) => id !== 'unassigned')
    );

    const filtered = recipients.filter((r) => !alreadyAssigned.has(r.team_id));
    return includeUnassignedOption
      ? [{ ...UNASSIGNED_OPTION, name: openLabel }, ...filtered]
      : filtered;
  }, [
    recipients,
    assignedRecipients,
    currentIndex,
    openLabel,
    includeUnassignedOption,
  ]);

  const selectedOption =
    options.find((o) => o.team_id === value) ||
    (includeUnassignedOption && value === 'unassigned'
      ? { ...UNASSIGNED_OPTION, name: openLabel }
      : null);

  return (
    <Autocomplete
      value={selectedOption}
      onChange={(_, newValue) => {
        onChange(
          newValue?.team_id || (includeUnassignedOption ? 'unassigned' : '')
        );
      }}
      options={options}
      getOptionLabel={(option) => option.name || ''}
      isOptionEqualToValue={(option, val) => option.team_id === val?.team_id}
      getOptionDisabled={(option) =>
        disabledRecipients.includes(option.team_id)
      }
      disabled={disabled}
      size="small"
      fullWidth
      disableClearable
      renderOption={(props, option) => {
        const { key, ...restProps } = props;
        return (
          <li key={key} {...restProps}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ minWidth: 0, py: 0.5 }}
            >
              {option.animal_food_rescuer_type && (
                <Pets
                  fontSize="small"
                  sx={{ color: 'text.secondary', flexShrink: 0 }}
                />
              )}
              <Stack sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {option.name}
                </Typography>
                {option.formatted_address && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      whiteSpace: 'normal',
                      lineHeight: 1.3,
                    }}
                  >
                    {option.formatted_address}
                  </Typography>
                )}
                <TaxExemptBadge
                  org={option}
                  sx={{ alignSelf: 'flex-start', mt: 0.5 }}
                />
              </Stack>
            </Stack>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label || t('donations.receiver')}
          placeholder={placeholder || t('donations.search-recipients')}
          onClick={(e) => e.stopPropagation()}
        />
      )}
      slotProps={{
        popper: {
          sx: { zIndex: 1500, maxWidth: '100vw' },
          placement: 'bottom-start',
        },
        paper: {
          sx: {
            minWidth: { xs: 'auto', sm: 360 },
            maxWidth: { xs: '90vw', sm: 'none' },
            maxHeight: 300,
          },
        },
      }}
      sx={{
        width: '100%',
      }}
    />
  );
};

export default RecipientAutocomplete;
